/**
 * Teacher's lessons list — upcoming + past, with Enter Classroom only when in window.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../api";
import { useTeacherAuth } from "../TeacherAuthContext";
import { MaterialCard, MaterialUploader } from "../components/Materials";

function fmtDateTime(iso) {
  return new Date(iso).toLocaleString([], {
    weekday: "long", day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}
function relTime(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  const min = Math.round(ms / 60000);
  if (min < -60 * 24) return `${Math.round(-min / 60 / 24)} days ago`;
  if (min < -60) return `${Math.round(-min / 60)}h ago`;
  if (min < 0) return `${-min} min ago`;
  if (min < 60) return `in ${min} min`;
  if (min < 60 * 24) return `in ${Math.round(min / 60)}h`;
  return `in ${Math.round(min / 60 / 24)} days`;
}

export default function TeacherLessons() {
  const { status, teacher, token, signingOutRef } = useTeacherAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [joining, setJoining] = useState(null);

  useEffect(() => {
    if (status === "anon" && !signingOutRef.current) {
      navigate("/teachers", { replace: true });
      return;
    }
    if (status === "auth" && teacher?.must_change_password) {
      navigate("/teachers/profile", { replace: true });
      return;
    }
    if (status !== "auth") return;
    let mounted = true;
    async function load() {
      try {
        const d = await api.teacherMyLessons(token);
        if (mounted) setData(d);
      } catch (err) {
        if (mounted) setError(err.message);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    // Re-tick every 30s so can_join_now flips on its own
    const t = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, [status, teacher, token, navigate, signingOutRef]);

  async function enterClassroom(lesson) {
    setJoining(lesson.id);
    setError("");
    try {
      const session = await api.teacherJoinLesson(token, lesson.id);
      navigate(`/classroom/${lesson.id}`, { state: session });
    } catch (err) {
      setError(err.message || "Could not enter classroom");
    } finally {
      setJoining(null);
    }
  }

  if (status !== "auth") return null;
  if (teacher?.must_change_password) return null;

  return (
    <main className="min-h-[100dvh] bg-[#FAF9F6] pt-20 md:pt-24 pb-24 px-6 md:px-10" data-testid="teacher-lessons-page">
      <div className="max-w-4xl mx-auto">
        <BackLink />
        <h1 className="font-display text-4xl md:text-6xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
          My lessons.
        </h1>

        {error && (
          <div className="mt-8 px-4 py-3 bg-[#B83A3A]/10 text-[#B83A3A] rounded-xl text-sm" data-testid="teacher-lessons-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="mt-10 text-[#1C1C1C]/60">Loading…</div>
        ) : (
          <>
            <Section
              title="Upcoming"
              testid="teacher-upcoming"
              items={data.upcoming}
              empty="No lessons assigned to you yet."
              renderItem={(l) => (
                <LessonRow
                  key={l.id}
                  l={l}
                  variant="upcoming"
                  joining={joining}
                  enterClassroom={enterClassroom}
                  token={token}
                />
              )}
            />

            <Section
              title="Past"
              testid="teacher-past"
              items={data.past}
              empty="Your past lessons will appear here."
              renderItem={(l) => (
                <LessonRow
                  key={l.id}
                  l={l}
                  variant="past"
                  token={token}
                />
              )}
            />
          </>
        )}
      </div>
    </main>
  );
}

function BackLink() {
  return (
    <Link
      to="/teachers/dashboard"
      className="inline-block text-[10px] tracking-[0.28em] uppercase text-[#1C1C1C]/55 hover:text-[#1C1C1C] mb-6 transition-colors"
    >
      ← Dashboard
    </Link>
  );
}

function Section({ title, testid, items, renderItem, empty }) {
  return (
    <section className="mt-10 md:mt-14" data-testid={testid}>
      <h2 className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-4">
        {title}
      </h2>
      {items.length === 0 ? (
        <div data-testid={`${testid}-empty`} className="bg-white border border-[#1C1C1C]/10 rounded-2xl p-8 text-center text-[#1C1C1C]/60">
          {empty}
        </div>
      ) : (
        <div className="space-y-3">{items.map(renderItem)}</div>
      )}
    </section>
  );
}

function LessonRow({ l, variant, joining, enterClassroom, token }) {
  const [open, setOpen] = useState(false);
  return (
    <article
      data-testid={`teacher-${variant}-item-${l.id}`}
      className="bg-white border border-[#1C1C1C]/10 rounded-2xl p-5 md:p-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#1C1C1C]/55 mb-1">
            {relTime(l.scheduled_start)}
          </div>
          <div className={`font-display ${variant === "upcoming" ? "text-xl md:text-2xl" : "text-lg"} text-[#1C1C1C] tracking-tight`}>
            {l.title}
          </div>
          <div className="text-sm text-[#1C1C1C]/60 mt-1">{fmtDateTime(l.scheduled_start)}</div>
          {l.student_name && (
            <div className="text-sm text-[#1C1C1C]/55 mt-1">Student: {l.student_name}</div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          {variant === "upcoming" ? (
            <>
              <button
                data-testid={`teacher-enter-${l.id}`}
                onClick={() => enterClassroom(l)}
                disabled={!l.can_join_now || joining === l.id}
                className="px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#1C1C1C]/85 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {joining === l.id ? "Opening…" : "Enter classroom →"}
              </button>
              {!l.can_join_now && (
                <span className="text-[10px] tracking-[0.22em] uppercase text-[#1C1C1C]/40">
                  Opens 10 min before start
                </span>
              )}
            </>
          ) : (
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#1C1C1C]/40">
              {l.has_feedback ? "Feedback received" : "Awaiting feedback"}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-[#1C1C1C]/8">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid={`teacher-materials-toggle-${l.id}`}
          className="text-[10px] tracking-[0.28em] uppercase text-[#1C1C1C]/60 hover:text-[#1C1C1C] transition-colors"
        >
          {open ? "Hide materials ↑" : "Materials ↓"}
        </button>
        {open && (
          <div className="mt-4">
            <LessonMaterialsPanel lessonId={l.id} token={token} />
          </div>
        )}
      </div>
    </article>
  );
}

function LessonMaterialsPanel({ lessonId, token }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      const list = await api.teacherListLessonMaterials(token, lessonId);
      setItems(list);
    } catch (e) {
      setError(e.message || "Could not load materials.");
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, token]);

  async function handleUpload(file) {
    setBusy(true);
    try {
      await api.teacherUploadMaterial(token, lessonId, file);
      await refresh();
      toast.success("Shared with student");
    } catch (e) {
      toast.error(e.message || "Upload failed.");
      throw e;
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(material) {
    await api.teacherDeleteMaterial(token, material.id);
    await refresh();
  }

  return (
    <div data-testid={`teacher-materials-panel-${lessonId}`}>
      {error && (
        <div className="mb-3 px-3 py-2 bg-[#B83A3A]/10 text-[#B83A3A] rounded-lg text-xs">{error}</div>
      )}
      <div className="space-y-2 mb-3">
        {(items || []).map((m) => (
          <MaterialCard
            key={m.id}
            material={m}
            fileUrl={api.teacherMaterialFileUrl(token, m.id)}
            onDelete={handleDelete}
          />
        ))}
        {items && items.length === 0 && (
          <div className="text-xs text-[#1C1C1C]/45 italic">No materials yet.</div>
        )}
      </div>
      <MaterialUploader compact busy={busy} onUpload={handleUpload} data-testid={`teacher-upload-${lessonId}`} />
    </div>
  );
}
